import * as THREE from "three";
import type { LocalPoint } from "../utils/geo";
import type { StructuresState } from "./structuresState";
import { defaultStructuresState } from "./structuresState";

export interface LayerVisibility {
  parcelBoundary: boolean;
  lotDimensions: boolean;
  roadContext: boolean;
  /** Existing structures on the parcel — Regrid Premium-schema building footprints. */
  buildingFootprints: boolean;
  terrain: boolean;
  contours: boolean;
  contourLabels: boolean;
  // Lot analysis layers (Phase 1).
  slopeHeatmap: boolean;
  slopeArrows: boolean;
  drainageFlow: boolean;
  elevationMarkers: boolean;
  // Sun + views layers (Phase 2).
  sunPath: boolean;
  sunShadows: boolean;
  solarGain: boolean;
  viewshed: boolean;
  bestViewpoints: boolean;
  sightlineArcs: boolean;
  viewpointMarker: boolean;
  // External overlays (Phase 3).
  floodOverlay: boolean;
  wetlandsOverlay: boolean;
  /** Esri World Imagery aerial photography draped on the terrain. */
  satelliteImagery: boolean;
  /** NLCD landcover classification draped on the terrain. */
  landcoverOverlay: boolean;
  /** NOAA Sea Level Rise (3 ft scenario) draped on the terrain. */
  seaLevelRiseOverlay: boolean;
  /** NOAA Hurricane Storm Surge (SLOSH Cat 3) draped on the terrain. */
  stormSurgeOverlay: boolean;
  /** USGS National Hydrography Dataset (streams, rivers, ponds). */
  hydrographyOverlay: boolean;
  // Decision polish (Phase 4).
  buildabilityHeatmap: boolean;
  drivewayCandidates: boolean;
  /** OSM-observed utility infrastructure (hydrants, power lines, etc.). */
  utilitiesOverlay: boolean;
}

export interface LayerHandles {
  parcelBoundary?: THREE.Object3D;
  lotDimensions?: THREE.Object3D;
  roadContext?: THREE.Object3D;
  buildingFootprints?: THREE.Object3D;
  terrain?: THREE.Object3D;
  contours?: THREE.Object3D;
  contourLabels?: THREE.Object3D;
  slopeHeatmap?: THREE.Object3D;
  slopeArrows?: THREE.Object3D;
  drainageFlow?: THREE.Object3D;
  elevationMarkers?: THREE.Object3D;
  sunPath?: THREE.Object3D;
  solarGain?: THREE.Object3D;
  viewshed?: THREE.Object3D;
  bestViewpoints?: THREE.Object3D;
  sightlineArcs?: THREE.Object3D;
  viewpointMarker?: THREE.Object3D;
  floodOverlay?: THREE.Object3D;
  wetlandsOverlay?: THREE.Object3D;
  satelliteImagery?: THREE.Object3D;
  landcoverOverlay?: THREE.Object3D;
  seaLevelRiseOverlay?: THREE.Object3D;
  stormSurgeOverlay?: THREE.Object3D;
  hydrographyOverlay?: THREE.Object3D;
  buildabilityHeatmap?: THREE.Object3D;
  drivewayCandidates?: THREE.Object3D;
  utilitiesOverlay?: THREE.Object3D;
}

export const defaultVisibility = (): LayerVisibility => ({
  parcelBoundary: true,
  lotDimensions: true,
  roadContext: true,
  buildingFootprints: false,
  terrain: true,
  contours: true,
  contourLabels: false,
  slopeHeatmap: false,
  slopeArrows: false,
  drainageFlow: false,
  elevationMarkers: true,
  sunPath: true,
  sunShadows: true,
  solarGain: false,
  viewshed: false,
  bestViewpoints: false,
  sightlineArcs: false,
  viewpointMarker: false,
  floodOverlay: true,
  wetlandsOverlay: true,
  satelliteImagery: false,
  landcoverOverlay: false,
  seaLevelRiseOverlay: false,
  stormSurgeOverlay: false,
  hydrographyOverlay: false,
  buildabilityHeatmap: false,
  drivewayCandidates: false,
  utilitiesOverlay: false
});

/**
 * "Gated preview" visibility — terrain + contours plus contextual
 * overlays the buyer expects to see at a glance (OSM roads, FEMA flood,
 * USFWS wetlands/waterways). Parcel polygon, analysis layers, and sun/
 * views chrome stay off until payment succeeds.
 */
export const blankVisibility = (): LayerVisibility => ({
  parcelBoundary: false,
  lotDimensions: false,
  roadContext: true,
  buildingFootprints: false,
  terrain: true,
  contours: true,
  contourLabels: false,
  slopeHeatmap: false,
  slopeArrows: false,
  drainageFlow: false,
  elevationMarkers: false,
  sunPath: false,
  sunShadows: false,
  solarGain: false,
  viewshed: false,
  bestViewpoints: false,
  sightlineArcs: false,
  viewpointMarker: false,
  floodOverlay: true,
  wetlandsOverlay: true,
  satelliteImagery: false,
  landcoverOverlay: false,
  seaLevelRiseOverlay: false,
  stormSurgeOverlay: false,
  hydrographyOverlay: false,
  buildabilityHeatmap: false,
  drivewayCandidates: false,
  utilitiesOverlay: false
});

export interface SunStudyState {
  /**
   * Date/time the sun-study features are evaluated at. The minute precision
   * is fine for shadow studies; we don't need sub-minute granularity.
   */
  dateTime: Date;
  /**
   * When true, the managed sun light + cast shadows are active. When false,
   * the original topo-map lighting in [createLights.ts](src/scene/createLights.ts)
   * stays untouched and the date/time only drives the sun path arc.
   */
  shadowsEnabled: boolean;
}

export interface ViewsStudyState {
  /**
   * Where the viewshed is computed FROM in local meters. `null` means use
   * the default reference point (best buildable region centroid, or the
   * parcel centroid if no buildable region has been computed yet).
   */
  viewpoint: LocalPoint | null;
  /** Eye height above ground in feet. Default ~8 ft (second-storey window). */
  eyeHeightFt: number;
}

export interface ViewState {
  visibility: LayerVisibility;
  sun: SunStudyState;
  views: ViewsStudyState;
  /** User-drawn structures (proposed boxes). */
  structures: StructuresState;
}

export { defaultStructuresState };

export function applyLayerVisibility(
  visibility: LayerVisibility,
  handles: LayerHandles
): void {
  setVisible(handles.parcelBoundary, visibility.parcelBoundary);
  setVisible(handles.lotDimensions, visibility.lotDimensions);
  setVisible(handles.roadContext, visibility.roadContext);
  setVisible(handles.buildingFootprints, visibility.buildingFootprints);
  setVisible(handles.terrain, visibility.terrain);
  setVisible(handles.contours, visibility.contours);
  setVisible(handles.contourLabels, visibility.contourLabels);
  setVisible(handles.slopeHeatmap, visibility.slopeHeatmap);
  setVisible(handles.slopeArrows, visibility.slopeArrows);
  setVisible(handles.drainageFlow, visibility.drainageFlow);
  setVisible(handles.elevationMarkers, visibility.elevationMarkers);
  setVisible(handles.sunPath, visibility.sunPath);
  setVisible(handles.solarGain, visibility.solarGain);
  setVisible(handles.viewshed, visibility.viewshed);
  setVisible(handles.bestViewpoints, visibility.bestViewpoints);
  setVisible(handles.sightlineArcs, visibility.sightlineArcs);
  setVisible(handles.viewpointMarker, visibility.viewpointMarker);
  setVisible(handles.floodOverlay, visibility.floodOverlay);
  setVisible(handles.wetlandsOverlay, visibility.wetlandsOverlay);
  setVisible(handles.satelliteImagery, visibility.satelliteImagery);
  setVisible(handles.landcoverOverlay, visibility.landcoverOverlay);
  setVisible(handles.seaLevelRiseOverlay, visibility.seaLevelRiseOverlay);
  setVisible(handles.stormSurgeOverlay, visibility.stormSurgeOverlay);
  setVisible(handles.hydrographyOverlay, visibility.hydrographyOverlay);
  setVisible(handles.buildabilityHeatmap, visibility.buildabilityHeatmap);
  setVisible(handles.drivewayCandidates, visibility.drivewayCandidates);
  setVisible(handles.utilitiesOverlay, visibility.utilitiesOverlay);
}

function setVisible(obj: THREE.Object3D | undefined, visible: boolean): void {
  if (!obj) return;
  obj.visible = visible;
}
